import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { KeyboardAvoidingView, Modal, Platform, StyleSheet, Text, TextInput, View } from '@/ui';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/auth';
import { messageFromError } from '@/api/errors';
import { requestStepUp, sendMfaCode } from '@/api/mfa';
import { BrandButton } from '@/components/ui/BrandButton';
import { useAuthStore } from '@/features/auth/authStore';
import { tokens } from '@/theme/tokens';
import { useAppTheme, type ThemeColors } from '@/theme/ThemeProvider';
import { runWithStepUp, StepUpCancelledError, type StepUpChallenge, type StepUpPrompt } from './stepUp';

// The high-value confirmation prompt (SEC-07, issue #159), mounted once at the
// root so every guarded money call shares one prompt instead of each screen
// growing its own. Screens reach it through useStepUp().

const CODE_LEN = 6;
/** Server recovery codes are 10 characters once the display dash is stripped. */
const RECOVERY_MIN = 10;
const RESEND_SECS = 30;

interface Pending {
  id: number;
  challenge: StepUpChallenge;
  resolve: (token: string) => void;
  reject: (e: unknown) => void;
}

const StepUpContext = createContext<StepUpPrompt | null>(null);

export function StepUpProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const pendingRef = useRef<Pending | null>(null);
  const nextId = useRef(0);

  const prompt = useCallback<StepUpPrompt>(
    (challenge) =>
      new Promise<string>((resolve, reject) => {
        // One prompt at a time. A second guarded call while one is open replaces
        // it; the first caller sees a cancellation, which charged nothing.
        pendingRef.current?.reject(new StepUpCancelledError());
        const next = { id: ++nextId.current, challenge, resolve, reject };
        pendingRef.current = next;
        setPending(next);
      }),
    [],
  );

  // Closes only the prompt it was called for, so a late answer from a replaced
  // prompt cannot close the one that replaced it.
  const done = useCallback((p: Pending) => {
    if (pendingRef.current !== p) return;
    pendingRef.current = null;
    setPending(null);
  }, []);

  // Never leave a caller awaiting forever (sign-out, a remount of the root).
  useEffect(() => () => pendingRef.current?.reject(new StepUpCancelledError()), []);

  return (
    <StepUpContext.Provider value={prompt}>
      {children}
      {pending ? <StepUpSheet key={pending.id} pending={pending} onDone={done} /> : null}
    </StepUpContext.Provider>
  );
}

/**
 * Wrap a guarded money call so a step_up_required refusal becomes a prompt and
 * one retry with the token, instead of an error on screen:
 *
 *   const withStepUp = useStepUp();
 *   withStepUp((stepUpToken) => purchaseSeasonPass(id, token, stepUpToken));
 *
 * Rejects with StepUpCancelledError when the fan closes the prompt — check it
 * with isStepUpCancelled and show nothing, since nothing was charged.
 */
export function useStepUp() {
  const prompt = useContext(StepUpContext);
  if (!prompt) throw new Error('useStepUp must be used inside <StepUpProvider>');
  return useCallback(
    <T,>(action: (stepUpToken?: string) => Promise<T>) => runWithStepUp(action, prompt),
    [prompt],
  );
}

function StepUpSheet({ pending, onDone }: { pending: Pending; onDone: (p: Pending) => void }) {
  const { t } = useTranslation('auth');
  const { colors: C } = useAppTheme();
  const styles = useMemo(() => createStyles(C), [C]);
  const { method } = pending.challenge;
  const [recovery, setRecovery] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  const send = useCallback(async () => {
    setError(null);
    setResendIn(RESEND_SECS);
    try {
      await sendMfaCode(useAuthStore.getState().accessToken as string);
    } catch (e) {
      setError(messageFromError(e));
      setResendIn(0);
    }
  }, []);

  // An SMS factor has nothing to type until a code is sent; an app always does.
  useEffect(() => {
    if (method === 'sms') void send();
  }, [method, send]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const id = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendIn]);

  const ready = recovery ? code.trim().length >= RECOVERY_MIN : code.length === CODE_LEN;

  const cancel = () => {
    if (submitting) return;
    pending.reject(new StepUpCancelledError());
    onDone(pending);
  };

  const submit = async () => {
    if (!ready || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const grant = await requestStepUp(useAuthStore.getState().accessToken as string, code.trim());
      pending.resolve(grant.step_up_token);
      onDone(pending);
    } catch (e) {
      // No factor to confirm with: a prompt cannot fix that, so hand the error
      // to the screen. Anything else — a wrong code, the rate limit, a dropped
      // connection — is worth another try from the same prompt.
      if (e instanceof ApiError && e.code === 'mfa_not_enrolled') {
        pending.reject(e);
        onDone(pending);
        return;
      }
      setError(messageFromError(e));
      setCode('');
      setSubmitting(false);
    }
  };

  const toggleRecovery = () => {
    setRecovery((r) => !r);
    setCode('');
    setError(null);
  };

  const body = recovery ? t('stepUpRecoveryBody') : method === 'sms' ? t('stepUpSmsBody') : t('stepUpAppBody');

  return (
    <Modal visible transparent animationType="fade" onRequestClose={cancel}>
      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={styles.backdrop}>
        <View style={styles.sheet}>
          <Text style={styles.title} accessibilityRole="header">
            {t('stepUpTitle')}
          </Text>
          <Text style={styles.body}>{body}</Text>

          <TextInput
            key={recovery ? 'recovery' : 'code'}
            value={code}
            onChangeText={(v) => setCode(recovery ? v.slice(0, 32) : v.replace(/\D/g, '').slice(0, CODE_LEN))}
            onSubmitEditing={submit}
            keyboardType={recovery ? 'default' : 'number-pad'}
            textContentType="oneTimeCode"
            importantForAutofill="no"
            autoComplete="off"
            autoCapitalize={recovery ? 'characters' : 'none'}
            autoCorrect={false}
            maxLength={recovery ? 32 : CODE_LEN}
            placeholder={recovery ? 'XXXXX-XXXXX' : '000000'}
            placeholderTextColor={C.textFaint}
            accessibilityLabel={recovery ? t('stepUpRecoveryLabel') : t('stepUpCodeLabel')}
            style={[styles.input, !recovery && styles.inputCode]}
            editable={!submitting}
            autoFocus
          />

          {error ? (
            <Text style={styles.error} accessibilityRole="alert">
              {error}
            </Text>
          ) : null}

          {method === 'sms' && !recovery ? (
            <Text style={styles.meta}>
              {t('didntGetIt')}{' '}
              {resendIn > 0 ? (
                <Text style={styles.metaDim}>{t('resendIn', { time: `0:${String(resendIn).padStart(2, '0')}` })}</Text>
              ) : (
                <Text style={styles.link} onPress={() => void send()}>
                  {t('resendCode')}
                </Text>
              )}
            </Text>
          ) : null}

          <Text style={[styles.meta, styles.link]} onPress={toggleRecovery} accessibilityRole="button">
            {recovery ? t('stepUpUseCode') : t('stepUpUseRecovery')}
          </Text>

          <View style={styles.actions}>
            <BrandButton variant="primary" disabled={!ready || submitting} onPress={submit}>
              {submitting ? t('verifying') : t('stepUpConfirm')}
            </BrandButton>
            <BrandButton variant="secondary" disabled={submitting} onPress={cancel}>
              {t('stepUpCancel')}
            </BrandButton>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

function createStyles(C: ThemeColors) {
  return StyleSheet.create({
    backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'center', paddingHorizontal: 24 },
    sheet: {
      width: '100%',
      maxWidth: 420,
      alignSelf: 'center',
      backgroundColor: C.surface,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: C.border,
      padding: 20,
    },
    title: { color: C.text, fontSize: 18, fontWeight: '700', marginBottom: 8 },
    body: { color: C.textDim, fontSize: 13.5, lineHeight: 20, marginBottom: 16 },
    input: {
      minHeight: 52,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: C.outline,
      backgroundColor: C.appBg,
      paddingHorizontal: 14,
      color: C.text,
      fontSize: 16,
    },
    inputCode: { fontSize: 22, letterSpacing: 8, textAlign: 'center' },
    error: { color: tokens.semantic.negative, fontSize: 13, marginTop: 10 },
    meta: { color: C.textDim, fontSize: 13, marginTop: 12 },
    metaDim: { color: C.textFaint },
    link: { color: tokens.accentMint, fontWeight: '600' },
    actions: { gap: 8, marginTop: 20 },
  });
}
